import { useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { View } from "react-native";
import { FormControlledDropdown } from "ui/molecules/form/FormControlledDropdown";
import { ReusableModal } from "ui/molecules/modal/ReusableModal";
import { useRouter } from "expo-router";
import { useGetAuditLogFiltersQuery } from "app/services/api/auditLogsApi";
import { useAuditLogFilterParams } from "app/features/auditLog/hooks/useAuditLogFilterParams";
import { FormButtons } from "app/features/residents/molecules/FormButtons";
import { FilterButton } from "ui/atoms/button/FilterButton";

interface AuditLogFilterForm {
  type: string | undefined;
  model_type: string | undefined;
  user_id: string | undefined;
}

export function AuditLogFilterComponent() {
  const [showMenu, setShowMenu] = useState(false);
  const params = useAuditLogFilterParams();

  const viewRef = useRef<View>(null);
  const router = useRouter();
  const { control, reset, handleSubmit } = useForm<AuditLogFilterForm>({
    defaultValues: {
      type: params.type,
      model_type: params.model_type,
      user_id: params.user_id,
    },
  });

  const { data: filters, isLoading } = useGetAuditLogFiltersQuery();

  const openMenu = () => setShowMenu(true);

  const handleClearAll = () => {
    const data = {
      type: undefined,
      model_type: undefined,
      user_id: undefined,
    };
    reset(data);
    router.setParams(data as Record<string, string | undefined>);
    setShowMenu(false);
  };

  const onSubmit = (data: AuditLogFilterForm) => {
    router.setParams({
      type: data.type ?? undefined,
      model_type: data.model_type ?? undefined,
      user_id: data.user_id ?? undefined,
    });
    setShowMenu(false);
  };

  const hasFilters = !!params.type || !!params.model_type || !!params.user_id;

  const actionOptions = useMemo(
    () =>
      (filters?.action_types || []).map((a) => ({
        value: a.value,
        label: a.label,
      })),
    [filters?.action_types],
  );

  const modelOptions = useMemo(
    () =>
      (filters?.model_types || []).map((m) => ({
        value: m.value,
        label: m.label,
      })),
    [filters?.model_types],
  );

  const userOptions = useMemo(
    () =>
      (filters?.users || []).map((u) => ({
        value: String(u.id),
        label: `${u.first_name} ${u.last_name} (${u.email})`,
      })),
    [filters?.users],
  );

  return (
    <>
      <View ref={viewRef}>
        <FilterButton onPress={openMenu} hasFilters={hasFilters} />
      </View>

      <ReusableModal
        title="Filter Audit Logs"
        isVisible={showMenu}
        onClose={() => setShowMenu(false)}
        className="w-full max-w-[600px] mx-auto"
      >
        <View className="grid md:grid-cols-2 gap-4">
          <FormControlledDropdown<AuditLogFilterForm>
            name="type"
            control={control}
            placeholder="Choose action"
            isDisabled={isLoading}
            options={actionOptions}
            label="Action"
            hideSearch
            canClear
          />
          <FormControlledDropdown<AuditLogFilterForm>
            name="model_type"
            control={control}
            placeholder="Choose model"
            isDisabled={isLoading}
            options={modelOptions}
            label="Model"
            hideSearch
            canClear
          />
          <FormControlledDropdown<AuditLogFilterForm>
            name="user_id"
            control={control}
            placeholder="Choose user"
            isDisabled={isLoading}
            options={userOptions}
            label="User"
            canClear
          />
        </View>
        <FormButtons
          okTitle="Apply"
          cancelTitle="Clear All"
          onClose={handleClearAll}
          onSave={handleSubmit(onSubmit)}
        />
      </ReusableModal>
    </>
  );
}
